import { SecretaryScope } from '../scopes';

/**
 * The browser's copy of the allow-list, used only to decide which calls are sent to the gateway (routing.ts).
 * The enforcing copy lives in the secretary-gw service (paziresh24/secretary-gw, src/allowlist.ts): this app no longer
 * holds the database, the grants or any token-exchange credential. Change both together.
 */

export type UpstreamService = 'gw' | 'v1' | 'drprofile' | 'hamdast';

export const UPSTREAM_BASES: Record<UpstreamService, string> = {
  gw: 'https://apigw.paziresh24.com',
  v1: 'https://api.paziresh24.com/V1',
  drprofile: 'https://drprofile.paziresh24.com',
  hamdast: 'https://hamdast.paziresh24.com',
};

interface Rule {
  service: UpstreamService;
  methods: string[];
  /** matched against the upstream path (no query) */
  path: RegExp;
  scope: SecretaryScope;
  /** the path carries a user id (first capture group): it must be the doctor's — or the secretary's own id, which the page sends because it is who is logged in; it is replaced by the doctor's */
  ownId?: boolean;
  /** answer fields the secretary may see (kept in `data`); everything else is dropped by the gateway */
  pick?: readonly string[];
}

/** the doctor's profile without national code, mobile, the secretary's number… */
const PROFILE_FIELDS = ['id', 'name', 'family', 'image', 'slug', 'medical_code', 'expertises', 'rate', 'rate_info'] as const;

const LUTHULI = '/v1/luthuli';
const N8N = '/v1/n8n-nelson/webhook';

/**
 * The only calls a secretary can make for a doctor. Deliberately left out: payments and settlement info, payment
 * rules acceptance, online-visit switches, prescriptions and insurance, the black list, tariffs and booking
 * settings, profile writes. A refused call is logged (secretary_audit, allowed=false) so a missing page
 * dependency is visible and can be added on purpose — never guess-open.
 */
export const RULES: Rule[] = [
  // ساعت کاری
  { service: 'gw', methods: ['GET'], path: new RegExp(`^${LUTHULI}/sample-duration$`), scope: 'schedule' },
  { service: 'gw', methods: ['POST'], path: new RegExp(`^${N8N}/(CompareWorkhoure|DeleteBookOutOfWorkHoure|workhour)$`), scope: 'schedule' },
  { service: 'gw', methods: ['GET'], path: new RegExp(`^${LUTHULI}/n8n-nelson/webhook/get-dr-service$`), scope: 'schedule' },
  { service: 'v1', methods: ['GET', 'POST'], path: /^\/doctor\/center\/workhours$/, scope: 'schedule' },
  // مرخصی
  { service: 'gw', methods: ['POST'], path: new RegExp(`^${N8N}/Notify$`), scope: 'vacation' },
  { service: 'v1', methods: ['GET', 'POST', 'PUT', 'DELETE'], path: /^\/doctor\/vacation\/\d+$/, scope: 'vacation' },
  { service: 'v1', methods: ['POST'], path: /^\/doctor\/books\/\d+\/(move|delete)$/, scope: 'vacation' },
  // مراجعین من
  { service: 'gw', methods: ['POST'], path: new RegExp(`^${N8N}/v6/allbooks$`), scope: 'appointments' },
  { service: 'gw', methods: ['POST'], path: new RegExp(`^${LUTHULI}/n8n-nelson/webhook/v1/booking-status/check$`), scope: 'appointments' },
  { service: 'gw', methods: ['GET'], path: new RegExp(`^${N8N}/v1/centers$`), scope: 'appointments' },
  { service: 'gw', methods: ['GET'], path: /^\/v1\/visit-channels$/, scope: 'appointments' },
  { service: 'v1', methods: ['GET'], path: /^\/doctor\/visit-channels$/, scope: 'appointments' },
  { service: 'gw', methods: ['GET'], path: new RegExp(`^${LUTHULI}/(user-center-services|visit-status)$`), scope: 'appointments' },
  { service: 'gw', methods: ['POST'], path: new RegExp(`^${LUTHULI}/visit-status/batch$`), scope: 'appointments' },
  { service: 'gw', methods: ['GET'], path: new RegExp(`^${LUTHULI}/n8n-nelson/webhook/PatientHistory$`), scope: 'appointments' },
  { service: 'gw', methods: ['GET', 'POST'], path: new RegExp(`^${LUTHULI}/n8n-nelson/webhook/book-comment$`), scope: 'appointments' },
  { service: 'gw', methods: ['GET'], path: new RegExp(`^${LUTHULI}/centers/\\d+/free-turns$`), scope: 'appointments' },
  { service: 'gw', methods: ['POST'], path: new RegExp(`^${LUTHULI}/(v2/suspend|panel/book/v2|patient-lookups)$`), scope: 'appointments' },
  { service: 'v1', methods: ['PUT'], path: /^\/doctor\/book\/came$/, scope: 'appointments' },
  { service: 'v1', methods: ['PATCH'], path: /^\/doctor\/book$/, scope: 'appointments' },
  { service: 'v1', methods: ['PATCH'], path: /^\/doctor\/centers\/\d+\/books\/\d+\/description$/, scope: 'appointments' },
  // read-only context the pages need
  { service: 'gw', methods: ['GET'], path: new RegExp(`^${LUTHULI}/holidays-next-year$`), scope: 'vacation' },
  { service: 'gw', methods: ['GET'], path: new RegExp(`^${LUTHULI}/holidays-next-year$`), scope: 'appointments' },
  { service: 'v1', methods: ['GET'], path: /^\/doctor\/profile$/, scope: 'appointments', pick: PROFILE_FIELDS },
  { service: 'gw', methods: ['GET'], path: /^\/v1\/doctor\/profile$/, scope: 'appointments', pick: PROFILE_FIELDS },
  { service: 'drprofile', methods: ['GET'], path: /^\/api\/users\/(\d+)\/centers$/, scope: 'appointments', ownId: true },
  { service: 'drprofile', methods: ['GET'], path: /^\/api\/users\/(\d+)\/centers$/, scope: 'vacation', ownId: true },
  { service: 'drprofile', methods: ['GET'], path: /^\/api\/users\/(\d+)\/centers$/, scope: 'schedule', ownId: true },
  { service: 'hamdast', methods: ['GET'], path: /^\/api\/v1\/widgets\/?$/, scope: 'appointments' },
];

/**
 * Client-side mirror of the allow-list: «could this call ever be made through the gateway?» (any scope, any id).
 * Only used to decide whether the browser sends a request to the gateway or leaves it alone (the app's own login check,
 * chat, search… keep going straight to their services). The server still decides every call.
 */
export const isGatewayRoutable = (service: string, method: string, path: string): boolean =>
  RULES.some(rule => rule.service === service && rule.methods.includes(method.toUpperCase()) && rule.path.test(path));

/** Which gateway service an absolute upstream URL belongs to, and its path (no query); longest base wins (V1 lives under api.paziresh24.com). */
export const resolveUpstream = (absoluteUrl: string): { service: UpstreamService; path: string; search: string } | null => {
  const [withoutQuery, ...rest] = absoluteUrl.split('?');
  const bases = (Object.entries(UPSTREAM_BASES) as [UpstreamService, string][]).sort((a, b) => b[1].length - a[1].length);
  for (const [service, base] of bases) {
    if (withoutQuery === base || withoutQuery.startsWith(`${base}/`)) {
      return { service, path: withoutQuery.slice(base.length) || '/', search: rest.length ? `?${rest.join('?')}` : '' };
    }
  }
  return null;
};

/**
 * Which token a path wants (measured against the live gateway): apigw's luthuli and n8n routes take the RS256 «gozargah»
 * token only; the legacy PHP-backed routes (`/v1/doctor/profile`, `/v1/visit-channels`) and the V1 API take the HS256 one;
 * drprofile and hamdast take either.
 */
export const bearerKindFor = (service: string, path: string): 'hs' | 'rs' =>
  service === 'gw' && /^\/v1\/(luthuli|n8n-nelson)(\/|$)/.test(path) ? 'rs' : 'hs';

/** keep only `keys` of the object under `data` (or of the body itself when there is no `data`); anything else is returned untouched */
export const pickFields = (body: unknown, keys: readonly string[]): unknown => {
  if (!body || typeof body !== 'object' || Array.isArray(body)) return body;
  const only = (source: Record<string, unknown>) => Object.fromEntries(Object.entries(source).filter(([key]) => keys.includes(key)));
  const record = body as Record<string, unknown>;
  const data = record.data;
  if (data && typeof data === 'object' && !Array.isArray(data)) return { ...record, data: only(data as Record<string, unknown>) };
  return only(record);
};

export type Decision = { ok: true; url: string; token: 'hs' | 'rs'; pick?: readonly string[] } | { ok: false; reason: string };

const UNSAFE_PATH = /(^|\/)\.\.?(\/|$)|\/\/|\\|%2e|%2f|%5c|%00|@/i;

/** Decide a proxied call. `path` starts with `/` and has no query; `search` is the raw `?…` (or ''). */
export const decide = (input: {
  service: string;
  method: string;
  path: string;
  search: string;
  doctorId: string;
  /** the logged-in secretary's own user id */
  callerId: string;
  scopes: readonly SecretaryScope[];
}): Decision => {
  const { service, method, path, search, doctorId, callerId, scopes } = input;
  if (!(service in UPSTREAM_BASES)) return { ok: false, reason: 'unknown_service' };
  if (!path.startsWith('/') || UNSAFE_PATH.test(path)) return { ok: false, reason: 'unsafe_path' };

  const rule = RULES.find(
    candidate =>
      candidate.service === service &&
      candidate.methods.includes(method) &&
      scopes.includes(candidate.scope) &&
      candidate.path.test(path) &&
      (!candidate.ownId || [doctorId, callerId].includes(candidate.path.exec(path)?.[1] ?? '')),
  );
  if (!rule) return { ok: false, reason: 'not_allowed' };

  // a path keyed by user id always ends up with the doctor's id, never anybody else's
  const upstreamPath = rule.ownId ? path.replace(/(\/api\/users\/)\d+/, `$1${doctorId}`) : path;

  const query = new URLSearchParams(search);
  // the widget list is keyed by user: only the doctor it acts for (the secretary's own id is replaced)
  if (service === 'hamdast') {
    const asked = query.get('user_id');
    if (asked !== doctorId && asked !== callerId) return { ok: false, reason: 'foreign_user' };
    query.set('user_id', doctorId);
  }
  const upstreamSearch = service === 'hamdast' ? `?${query.toString()}` : search;

  const base = UPSTREAM_BASES[service as UpstreamService];
  const url = `${base}${upstreamPath}${upstreamSearch}`;
  if (new URL(url).origin !== new URL(base).origin) return { ok: false, reason: 'origin_changed' };
  return { ok: true, url, token: bearerKindFor(service, upstreamPath), ...(rule.pick ? { pick: rule.pick } : {}) };
};
